// The News area's calls to Loggione, as TanStack Query hooks. The components
// never see the api: a page calls a hook here and hands the result to
// src/features/news/adapt.ts before it reaches a component.
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";

import { api, isApiError, unwrap } from "../../api/client";
import { keys } from "../../api/keys";
import type { components } from "../../api/schema";
import type { SavedFilter } from "./params";
import { applySavedToSearch, withoutSearchBookmarks, type SearchPage } from "../search/adapt";
import {
  applyFollowChange,
  applySavedToDetail,
  applySavedToFeed,
  applySavedToPages,
  applySavedToSitting,
  clearedSavedPage,
  inverseFollowChange,
  withoutBookmark,
  withoutBookmarks,
  withoutBookmarksInSitting,
  type Catalogue,
  type FeedPage,
  type ItemDetail,
  type Origin,
  type SavedChange,
  type SavedPage,
  type SittingDetail,
} from "./adapt";

export type { Origin, SavedChange };
export type FeedBucket = "new" | "previous";
/** The scope of the lists of Il mio feed: the pinned sources (the virtual folder «Preferiti»). */
export const FAVOURITES_SCOPE = "favourites";
/** What a list is narrowed to besides `sources`: one of the user's feeds, the favourites, or nothing (Ultime news). */
export type FeedScope = typeof FAVOURITES_SCOPE | { feedId: string } | null;
export type FollowChange = components["schemas"]["FollowChange"];
export type SavedState = components["schemas"]["SavedState"];

export const PAGE_SIZE = 20;
/** How long an item's detail stays fresh: five minutes. */
const ITEM_STALE_TIME = 5 * 60_000;

/** The catalogue of sources: organisations, sections, health, favourites. */
export function useSources() {
  return useQuery({
    queryKey: keys.sources,
    queryFn: async () => unwrap((await api().GET("/api/v1/sources")).data),
  });
}

/**
 * POST /me/visit once per opening of the News area: the answer's `since`
 * is where «News dall'ultimo accesso» and «News precedenti» split. Kept
 * for the whole page load: a refetch would move the split under the user.
 */
export function useVisit() {
  return useQuery({
    queryKey: keys.visit,
    queryFn: async () => unwrap((await api().POST("/api/v1/me/visit")).data),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

/**
 * One list of Ultime news, paged by cursor; `sources` null means every source.
 * When the filter changes (a checkbox, a chip clicked) the list keeps the
 * cards of the last filter until the new ones arrive (placeholder data), so
 * the page does not empty under the user. With a feed as `scope` (Il mio
 * feed), a 404 means the feed was deleted elsewhere: the user's feeds are
 * asked again, so the panel drops it and «Preferiti» takes its place. With
 * the favourites as `scope`, the list is GET /feed?favourites=true.
 */
export function useFeed(bucket: FeedBucket, sources: string[] | null, scope: FeedScope = null, enabled = true) {
  const queryClient = useQueryClient();
  const feedId = scope !== null && scope !== FAVOURITES_SCOPE ? scope.feedId : null;
  const favourites = scope === FAVOURITES_SCOPE;
  return useInfiniteQuery({
    queryKey: keys.feed(bucket, sources, favourites ? FAVOURITES_SCOPE : feedId),
    queryFn: async ({ pageParam }) => {
      try {
        return unwrap(
          (
            await api().GET("/api/v1/feed", {
              params: {
                query: {
                  bucket,
                  limit: PAGE_SIZE,
                  ...(sources ? { sources } : {}),
                  ...(feedId ? { feed_id: feedId } : {}),
                  ...(favourites ? { favourites: true } : {}),
                  ...(pageParam ? { cursor: pageParam } : {}),
                },
              },
            })
          ).data,
        );
      } catch (error) {
        if (feedId && isApiError(error) && error.status === 404)
          void queryClient.invalidateQueries({ queryKey: keys.feeds });
        throw error;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * One item with its text, for the card expanded in place: asked when the card
 * opens (`enabled`), kept in the cache under keys.item. The api marks the
 * item as seen and logs the opening at every answer, and a text can weigh
 * megabytes: the answer stays fresh for five minutes (closing and reopening a
 * card asks nothing) and is not asked again when the window gets the focus.
 * When a detail already in the cache is served again, the index of the
 * document (keys.sections) is asked again too: its offsets hold on the text
 * served with it, and a record in sitting grows between two answers.
 */
export function useItem(origin: Origin, id: string, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: keys.item(origin, id),
    queryFn: async () => {
      const reload = queryClient.getQueryData<ItemDetail>(keys.item(origin, id)) !== undefined;
      const detail = unwrap(
        (await api().GET("/api/v1/items/{origin}/{item_id}", { params: { path: { origin, item_id: id } } })).data,
      );
      if (reload) void queryClient.invalidateQueries({ queryKey: keys.sections(origin, id) });
      return detail;
    },
    staleTime: ITEM_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled,
  });
}

/**
 * News salvate, paged by cursor: the bookmarks of the whole months of
 * `months` (YYYY-MM) and of the single weeks of `weeks` (YYYY-MM-Www), both
 * null for every bookmark. When the filter changes the cards and the facets
 * of the last filter stand in until the new page arrives (placeholder
 * data): the date filter panel never empties under the user.
 */
export function useSaved({ months, weeks }: SavedFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.saved(months, weeks),
    queryFn: async ({ pageParam }) =>
      unwrap(
        (
          await api().GET("/api/v1/me/saved", {
            params: {
              query: {
                limit: PAGE_SIZE,
                ...(months ? { months } : {}),
                ...(weeks ? { weeks } : {}),
                ...(pageParam ? { cursor: pageParam } : {}),
              },
            },
          })
        ).data,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * PUT /me/sources: add or remove favourites. The cached catalogue takes the
 * change at once (the checkbox follows the click); a refused change is
 * undone by applying its inverse, so a later change already in flight is not
 * lost. The PUTs share one scope: they go to the api one at a time, in the
 * order of the clicks, while onMutate stays immediate. On settle the catalogue,
 * the feed lists and the user's feeds are refetched, but only by the last change of the queue,
 * so no refetch overwrites a change still waiting.
 */
export function useFollowSources() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.followSources,
    scope: { id: "follow-sources" },
    mutationFn: async (change: FollowChange) => unwrap((await api().PUT("/api/v1/me/sources", { body: change })).data),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: keys.sources });
      queryClient.setQueryData<Catalogue>(keys.sources, (old) => (old ? applyFollowChange(old, change) : old));
    },
    onError: (_error, change) => {
      queryClient.setQueryData<Catalogue>(keys.sources, (old) =>
        old ? applyFollowChange(old, inverseFollowChange(change)) : old,
      );
    },
    onSettled: async () => {
      // this mutation still counts as pending here: 1 means nothing is queued behind it
      if (queryClient.isMutating({ mutationKey: keys.followSources }) !== 1) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.sources }),
        queryClient.invalidateQueries({ queryKey: keys.feedAll }),
        // an unpinned source leaves every feed of the user (Il mio feed), and pinning it again does not bring it back
        queryClient.invalidateQueries({ queryKey: keys.feeds }),
      ]);
    },
  });
}

/**
 * The queries a bookmark shows in: every feed list, every page of News salvate, every item detail, every sitting's
 * documents, every page of the search's results.
 */
const BOOKMARK_KEYS = [keys.feedAll, keys.savedAll, keys.itemAll, keys.sittingAll, keys.searchAll];
/**
 * The lists a bookmark change cancels the refetch of, so an answer already on
 * its way does not put the old bookmark back over the new one: the feed
 * lists, News salvate and the documents of a sitting (asked again every
 * minute while it is open); not a page on its way («Carica altre»), never an
 * item's detail.
 */
const BOOKMARK_LISTS = [keys.feedAll, keys.savedAll, keys.sittingAll, keys.searchAll];

/** The variables of a mutation under keys.saveItem, as the cache hands them back untyped. */
const isSavedChange = (value: unknown): value is SavedChange =>
  typeof value === "object" && value !== null && "origin" in value && "id" in value && "saved" in value;

/** The query of a list fetching its first page or refetching, not its next page. */
const notFetchingMore = (query: { state: { fetchMeta: { fetchMore?: unknown } | null } }) =>
  query.state.fetchMeta?.fetchMore === undefined;

/**
 * PUT or DELETE /me/saved/{origin}/{item_id}. The bookmark changes at once in
 * every list in the cache (the feed lists, the pages of News salvate, where
 * the card leaves and its month's count drops, the item details, the
 * documents of a sitting of the Parliament); every
 * query touched is snapshotted first and put back whole if the api refuses,
 * then the changes still queued behind are applied again, so a refused
 * bookmark never undoes a later one. The calls share one scope: they reach
 * the api one at a time, in the order of the clicks (two clicks on one card
 * are a PUT then a DELETE, never both at once), while onMutate stays
 * immediate. On settle every list is refetched, but only by the last call of
 * the queue, so no refetch overwrites a change still waiting.
 */
export function useSaveItem() {
  const queryClient = useQueryClient();

  const apply = (change: SavedChange) => {
    queryClient.setQueriesData<InfiniteData<FeedPage>>({ queryKey: keys.feedAll }, (old) =>
      old ? { ...old, pages: old.pages.map((page) => applySavedToFeed(page, change)) } : old,
    );
    queryClient.setQueriesData<InfiniteData<SavedPage>>({ queryKey: keys.savedAll }, (old) =>
      old ? { ...old, pages: applySavedToPages(old.pages, change) } : old,
    );
    queryClient.setQueriesData<ItemDetail>({ queryKey: keys.itemAll }, (old) =>
      old ? applySavedToDetail(old, change) : old,
    );
    queryClient.setQueriesData<SittingDetail>({ queryKey: keys.sittingAll }, (old) =>
      old ? applySavedToSitting(old, change) : old,
    );
    queryClient.setQueriesData<InfiniteData<SearchPage>>({ queryKey: keys.searchAll }, (old) =>
      old ? { ...old, pages: old.pages.map((page) => applySavedToSearch(page, change)) } : old,
    );
  };

  return useMutation({
    mutationKey: keys.saveItem,
    scope: { id: "save-item" },
    mutationFn: async ({ origin, id, saved }: SavedChange) => {
      const params = { params: { path: { origin, item_id: id } } };
      const result = saved
        ? await api().PUT("/api/v1/me/saved/{origin}/{item_id}", params)
        : await api().DELETE("/api/v1/me/saved/{origin}/{item_id}", params);
      return unwrap(result.data);
    },
    onMutate: async (change) => {
      await Promise.all(
        BOOKMARK_LISTS.map((queryKey) => queryClient.cancelQueries({ queryKey, predicate: notFetchingMore })),
      );
      const snapshots = BOOKMARK_KEYS.flatMap((queryKey) => queryClient.getQueriesData({ queryKey }));
      apply(change);
      // the context names this call among the queued ones (its variables may be sent again by «Riprova»)
      return { snapshots };
    },
    onError: (_error, _change, context) => {
      for (const [queryKey, data] of context?.snapshots ?? []) queryClient.setQueryData(queryKey, data);
      // the calls queued behind this one took their snapshots after its change: their own changes go back on
      for (const mutation of queryClient
        .getMutationCache()
        .findAll({ mutationKey: keys.saveItem, status: "pending" })) {
        if (mutation.state.context !== context && isSavedChange(mutation.state.variables))
          apply(mutation.state.variables);
      }
    },
    onSettled: async () => {
      // this mutation still counts as pending here: 1 means nothing is queued behind it
      if (queryClient.isMutating({ mutationKey: keys.saveItem }) !== 1) return;
      // «Pulisci tutto» queued behind: it refetches when it is done
      if (queryClient.isMutating({ mutationKey: keys.clearSaved }) > 0) return;
      await Promise.all(BOOKMARK_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

/**
 * DELETE /me/saved, «Pulisci tutto»: every bookmark of the caller goes,
 * whatever months News salvate is filtered by. The cache follows at once:
 * every page of News salvate is empty (no card, no facet: the empty state),
 * every card of the feed lists and every item detail loses its bookmark;
 * every query touched is snapshotted first and put back whole if the api
 * refuses. It shares the bookmarks' queue, so a bookmark clicked just before
 * reaches the api first and the clearing takes it too. On settle every list
 * is refetched, unless a bookmark is still queued: that one refetches at
 * the end, so no refetch overwrites a change still waiting.
 */
export function useClearSaved() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.clearSaved,
    scope: { id: "save-item" },
    mutationFn: async () => unwrap((await api().DELETE("/api/v1/me/saved")).data),
    onMutate: async () => {
      await Promise.all(
        BOOKMARK_LISTS.map((queryKey) => queryClient.cancelQueries({ queryKey, predicate: notFetchingMore })),
      );
      const snapshots = BOOKMARK_KEYS.flatMap((queryKey) => queryClient.getQueriesData({ queryKey }));
      queryClient.setQueriesData<InfiniteData<FeedPage>>({ queryKey: keys.feedAll }, (old) =>
        old ? { ...old, pages: old.pages.map(withoutBookmarks) } : old,
      );
      queryClient.setQueriesData<InfiniteData<SavedPage>>({ queryKey: keys.savedAll }, (old) =>
        old ? { pages: [clearedSavedPage()], pageParams: [null] } : old,
      );
      queryClient.setQueriesData<ItemDetail>({ queryKey: keys.itemAll }, (old) => (old ? withoutBookmark(old) : old));
      queryClient.setQueriesData<SittingDetail>({ queryKey: keys.sittingAll }, (old) =>
        old ? withoutBookmarksInSitting(old) : old,
      );
      queryClient.setQueriesData<InfiniteData<SearchPage>>({ queryKey: keys.searchAll }, (old) =>
        old ? { ...old, pages: old.pages.map(withoutSearchBookmarks) } : old,
      );
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.snapshots ?? []) queryClient.setQueryData(queryKey, data);
    },
    onSettled: async () => {
      if (queryClient.isMutating({ mutationKey: keys.saveItem }) > 0) return;
      await Promise.all(BOOKMARK_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}
